<script setup lang="ts">
import { computed, inject, reactive, ref, Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { UkazovatelHodnota, UkazovatelRef } from '/@src/client-portal'
import {
  MonitorovanyUkazovatelPublicResponse,
  VyzvaSpecificationPublicResponse,
} from '/@src/client-public'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface UkazovateleProjektu {
  ukazovatele: any
  isValidSection: boolean
}

const props = withDefaults(defineProps<UkazovateleProjektu>(), {
  ukazovatele: undefined,
  isValidSection: false,
})

const { t } = useI18n()
const steps = useSteps()
const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')
const stavPodana = inject<boolean>('stavPodana', false)

const ukazovateleData = computed(() => props.ukazovatele)

function getUkazovatel(ukazovatelId: UkazovatelRef['ukazovatelId']) {
  return specResponse?.zoppmSpec?.monitorovaneUkazovatele?.find(
    (ukazovatel) => ukazovatel?.ukazovatelRef?.ukazovatelId === ukazovatelId
  )
}

function getHodnota(
  hodnota: number | string,
  ukazovatel: MonitorovanyUkazovatelPublicResponse,
  logickaHodnota: boolean = false
) {
  // Pri logickej hodnote neprichádza merná jednotka
  if (logickaHodnota && !ukazovatel?.ukazovatelRef?.mernaJednotka) {
    switch (hodnota as unknown) {
      case 'true':
        return t('zoppm.step4.part7.radioYes')
      case '"true"':
        return t('zoppm.step4.part7.radioYes')
      case 'false':
        return t('zoppm.step4.part7.radioNo')
      case '"false"':
        return t('zoppm.step4.part7.radioNo')
      default:
        return t('zoppm.step4.part7.radioDefault')
    }
  }

  if (
    !ukazovatel ||
    (ukazovatel?.textovaHodnotaSpec &&
      ukazovatel?.textovaHodnotaSpec?.povoleneHodnoty?.length === 0)
  ) {
    return hodnota
  }

  return ukazovatel?.textovaHodnotaSpec?.povoleneHodnoty?.[parseInt(hodnota as string)]
}

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.ukazovatele'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.ukazovatele.mainHeading')
)
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>

  <ZhrnutieError v-if="isValidSection" />

  <template v-for="ukazovatel in ukazovateleData" :key="ukazovatel.ukazovatelVyzvyRef">
    <IDSKHeading as="h3" variant="s" class="mb-2">
      {{ ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.nazov }}
    </IDSKHeading>
    <IDSKParagraph
      v-if="
        getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId)?.popis
      "
      class="mb-6"
    >
      {{
        getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId)?.popis
      }}
    </IDSKParagraph>

    <DisplayList
      variant="inline"
      inline-layout="one-half"
      class="govuk-!-width-two-thirds mb-6"
    >
      <dt
        v-if="
          ukazovatel.vychodiskovaHodnota?.textovaHodnota != null ||
          (ukazovatel?.vychodiskovaHodnota?.logickaHodnota !== null
            ? JSON.stringify(ukazovatel?.vychodiskovaHodnota?.logickaHodnota)
            : null) ||
          ukazovatel.vychodiskovaHodnota?.ciselnaHodnota?.toString() != null
        "
        class="mb-6"
      >
        {{ t('zoppm.zhrnutie.ukazovatele.defaultValue') }}:
      </dt>
      <dd
        v-if="
          ukazovatel.vychodiskovaHodnota?.textovaHodnota != null ||
          (ukazovatel?.vychodiskovaHodnota?.logickaHodnota !== null
            ? JSON.stringify(ukazovatel?.vychodiskovaHodnota?.logickaHodnota)
            : null) ||
          ukazovatel.vychodiskovaHodnota?.ciselnaHodnota?.toString() != null
        "
        class="d-flex align-items-right"
      >
        {{
          getHodnota(
            ukazovatel.vychodiskovaHodnota?.textovaHodnota ||
              (ukazovatel?.vychodiskovaHodnota?.logickaHodnota !== null
                ? JSON.stringify(ukazovatel?.vychodiskovaHodnota?.logickaHodnota)
                : null) ||
              ukazovatel.vychodiskovaHodnota.ciselnaHodnota?.toString(),
            getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId),
            ukazovatel.vychodiskovaHodnota?.textovaHodnota == null
          )
        }}
        {{
          ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.mernaJednotka ||
          getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId)
            ?.ukazovatelRef?.mernaJednotka
        }}
      </dd>
      <dt
        v-if="
          ukazovatel.cielovaHodnota?.textovaHodnota != null ||
          (ukazovatel?.cielovaHodnota?.logickaHodnota !== null
            ? JSON.stringify(ukazovatel?.cielovaHodnota?.logickaHodnota)
            : null) ||
          ukazovatel.cielovaHodnota?.ciselnaHodnota?.toString() != null
        "
      >
        {{ t('zoppm.zhrnutie.ukazovatele.goalValue') }}:
      </dt>
      <dd
        v-if="
          ukazovatel.cielovaHodnota?.textovaHodnota != null ||
          (ukazovatel?.cielovaHodnota?.logickaHodnota !== null
            ? JSON.stringify(ukazovatel?.cielovaHodnota?.logickaHodnota)
            : null) != null ||
          ukazovatel.cielovaHodnota?.ciselnaHodnota?.toString() != null
        "
        class="d-flex align-items-right"
      >
        {{
          getHodnota(
            ukazovatel.cielovaHodnota?.textovaHodnota ||
              (ukazovatel?.cielovaHodnota?.logickaHodnota !== null
                ? JSON.stringify(ukazovatel?.cielovaHodnota?.logickaHodnota)
                : null) ||
              ukazovatel.cielovaHodnota?.ciselnaHodnota?.toString(),
            getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId),
            ukazovatel.cielovaHodnota?.textovaHodnota == null
          )
        }}
        {{
          ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.mernaJednotka ||
          getUkazovatel(ukazovatel.ukazovatelVyzvyRef.ukazovatelRef?.ukazovatelId)
            ?.ukazovatelRef?.mernaJednotka
        }}
      </dd>
    </DisplayList>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-ukazovatel-projekt-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['ukazovateleProjektu'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>
